<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { useSidebarDropdownAnchor } from './useSidebarDropdownAnchor';

const store = useStore();
const router = useRouter();
const isOpen = ref(false);
const dropdownRef = ref(null);

// Menu fica no rodapé da sidebar, então abre pra cima.
// Teleport pro <body>: a sidebar tem position:relative + z-index:40 e
// prende qualquer absolute interno no próprio contexto de empilhamento.
const { triggerRef, anchorStyle, menuMaxHeight, measure } =
  useSidebarDropdownAnchor({ placement: 'top' });

let intervalId = null;

const todayCount = computed(() => store.getters['tasks/getTodayCount'] || 0);
const overdueCount = computed(
  () => store.getters['tasks/getOverdueCount'] || 0
);
const notificationsTasks = computed(
  () => store.getters['tasks/getNotificationsTasks'] || []
);

// IDs já vistos — ref REATIVO (localStorage sozinho não dispara recálculo do
// badge no Vue; por isso o botão "marcar como vistas" não funcionava antes).
const loadSeenIds = () => {
  try {
    return new Set(
      JSON.parse(localStorage.getItem('tasks_bell_seen_ids') || '[]')
    );
  } catch (e) {
    return new Set();
  }
};
const seenIds = ref(loadSeenIds());

const badge = computed(() => {
  const tasks = notificationsTasks.value || [];
  const newCount = tasks.filter(t => !seenIds.value.has(t.id)).length;
  if (!newCount) return '';
  return newCount > 99 ? '99+' : String(newCount);
});

const fetchOnce = () => {
  store.dispatch('tasks/fetchNotifications').catch(() => {});
};

const toggle = () => {
  measure();
  isOpen.value = !isOpen.value;
  if (isOpen.value) fetchOnce();
};

const goToTasks = () => {
  isOpen.value = false;
  router.push({ name: 'tasks' });
};

const markAllSeen = () => {
  try {
    const ids = (notificationsTasks.value || []).map(t => t.id);
    localStorage.setItem('tasks_bell_seen_ids', JSON.stringify(ids));
    localStorage.setItem('tasks_bell_seen_at', String(Date.now()));
    // Atualiza o ref reativo → o badge recalcula e some na hora.
    seenIds.value = new Set(ids);
    isOpen.value = false;
  } catch (e) {
    /* silent */
  }
};

const openTask = task => {
  isOpen.value = false;
  router.push({ name: 'tasks', query: { open: String(task.id) } });
};

const formatDue = iso => {
  try {
    const d = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dDay = new Date(d);
    dDay.setHours(0, 0, 0, 0);
    const pad = n => String(n).padStart(2, '0');
    const diffDays = Math.floor((dDay - today) / 86400000);
    const time = pad(d.getHours()) + ':' + pad(d.getMinutes());
    if (diffDays < 0) return Math.abs(diffDays) + 'd atrás';
    if (diffDays === 0) return 'Hoje ' + time;
    if (diffDays === 1) return 'Amanhã ' + time;
    return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + ' ' + time;
  } catch (e) {
    return '';
  }
};

const isOverdue = t => {
  if (!t.due_at) return false;
  return new Date(t.due_at) < new Date();
};

const priorityClass = p =>
  ({
    urgent: 'bg-n-ruby-9',
    high: 'bg-n-amber-9',
    medium: 'bg-n-blue-9',
    low: 'bg-n-slate-8',
    none: 'bg-n-slate-6',
  })[p] || 'bg-n-slate-8';

onMounted(() => {
  fetchOnce();
  intervalId = setInterval(fetchOnce, 60000);
});
onUnmounted(() => {
  if (intervalId) clearInterval(intervalId);
});

// Conteúdo é teleportado pro <body> — sem ignore, clique DENTRO do menu
// contaria como "fora" do dropdownRef e fecharia na hora.
onClickOutside(
  dropdownRef,
  () => {
    isOpen.value = false;
  },
  { ignore: ['[data-sidebar-tasks-menu]'] }
);
</script>

<template>
  <div ref="dropdownRef" class="relative">
    <button
      ref="triggerRef"
      class="relative grid flex-shrink-0 rounded-xl size-8 hover:bg-n-alpha-2 place-content-center text-n-slate-11 hover:text-n-slate-12"
      :title="
        badge ? `${todayCount} hoje, ${overdueCount} atrasadas` : 'Tarefas'
      "
      @click="toggle"
    >
      <span class="size-4 i-ri-list-check" />
      <span
        v-if="badge"
        class="absolute grid px-1 leading-none rounded-full min-h-4 min-w-4 -top-1 -right-1 bg-n-ruby-9 place-items-center text-[9px] font-semibold text-white"
      >
        {{ badge }}
      </span>
    </button>
    <TeleportWithDirection>
      <div
        v-if="isOpen"
        data-sidebar-tasks-menu
        class="fixed z-[100] flex flex-col overflow-hidden border shadow-xl w-80 max-h-[480px] bg-n-solid-2 rounded-2xl border-n-strong"
        :style="{ ...anchorStyle, maxHeight: menuMaxHeight }"
      >
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">Tarefas</h3>
          <div class="flex items-center gap-1.5 text-xs">
            <span
              v-if="overdueCount"
              class="px-1.5 py-0.5 rounded-full font-medium bg-n-ruby-9/15 text-n-ruby-11"
            >
              {{ overdueCount }} atrasada{{ overdueCount > 1 ? 's' : '' }}
            </span>
            <span
              v-if="todayCount"
              class="px-1.5 py-0.5 rounded-full font-medium bg-n-brand/15 text-n-blue-11"
            >
              {{ todayCount }} hoje
            </span>
          </div>
        </div>
        <div class="flex-1 overflow-y-auto">
          <div
            v-if="notificationsTasks.length === 0"
            class="flex flex-col items-center gap-2 px-4 py-8 text-center"
          >
            <span
              class="grid rounded-2xl size-9 place-items-center bg-n-slate-3 text-n-slate-11"
            >
              <span class="size-[18px] i-ri-check-double-line" />
            </span>
            <p class="m-0 text-sm font-medium text-n-slate-12">Tudo em dia</p>
            <p class="m-0 text-xs text-n-slate-10">
              Nenhuma tarefa urgente por aqui.
            </p>
          </div>
          <ul v-else class="divide-y divide-n-weak">
            <li
              v-for="t in notificationsTasks"
              :key="t.id"
              class="flex items-start gap-2.5 px-4 py-2.5 cursor-pointer hover:bg-n-alpha-2"
              @click="openTask(t)"
            >
              <span
                class="size-2 rounded-full inline-block shrink-0 mt-1.5"
                :class="priorityClass(t.priority)"
              />
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium truncate text-n-slate-12">
                  {{ t.title }}
                </div>
                <div
                  v-if="t.due_at"
                  class="text-xs mt-0.5"
                  :class="
                    isOverdue(t)
                      ? 'text-n-ruby-11 font-semibold'
                      : 'text-n-slate-10'
                  "
                >
                  {{ formatDue(t.due_at) }}
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div class="flex border-t border-n-weak">
          <button
            v-if="notificationsTasks.length > 0"
            class="flex items-center justify-center flex-1 gap-1.5 px-4 py-2.5 text-xs font-medium transition-colors text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :title="`Limpar contador (${notificationsTasks.length} tarefas)`"
            @click="markAllSeen"
          >
            <span class="size-3.5 i-ri-check-line" />
            Marcar como vistas
          </button>
          <button
            class="flex items-center justify-center flex-1 gap-1.5 px-4 py-2.5 text-xs font-medium transition-colors text-n-blue-11 hover:bg-n-brand/10"
            @click="goToTasks"
          >
            Ver todas
            <span class="size-3.5 i-ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </TeleportWithDirection>
  </div>
</template>
